import {
  Avatar,
  Box,
  Collapse,
  Grid,
  Hidden,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { ClassNameMap, CSSProperties } from '@material-ui/styles';
import { format } from 'date-fns';
import React, { FC, ReactNode, useState } from 'react';
import { ServiceSummary } from '../ssm.types';
import { useTranslation } from '../../../hooks/useTranslation';
import colors from '../../../themes/main-colors';
import { formatServiceAddress } from '../../../util/format';
import ROUTES from '../../../routes';

type Props = {
  summary: ServiceSummary;
  isPulldown?: boolean;
  service?: string;
};

const desktop: CSSProperties = {
  border: `1px solid ${colors.sparkBlue}`,
  borderRadius: '5px',
  padding: '1em',
  position: 'relative',
  boxShadow: `0px 3px 6px ${colors.boxShadowColor}`,
};

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    fontWeight: 'bold',
    color: colors.lightGray4,
  },
  mobileHeader: {
    fontWeight: 'bold',
    fontSize: 'unset',
    color: colors.lightGray4,
  },
  value: {
    fontWeight: 'bold',
  },
  desktop,
  mobile: {
    ...desktop,
    padding: '0.7em',
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0',
  },
  pulldown: {
    position: 'absolute',
    right: '20px',
    top: '6px',
    backgroundColor: colors.sparkBlue,
    height: '30px',
    width: '30px',
  },
  summaryContainer: {
    marginBottom: '0.5em',
  },
}));

type PulldownProps = {
  children?: ReactNode;
  service?: string;
};

const Pulldown: FC<PulldownProps> = ({ children, service }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <>
      <Hidden smUp>
        <Typography className={classes.mobileHeader}>
          {`${t(String(service))} ${t('SERVICE')} ${t(
            'SUMMARY',
          )}`.toUpperCase()}
        </Typography>
      </Hidden>

      <Collapse in={open}>
        <Box mt={1}>{children}</Box>
      </Collapse>

      <Avatar
        data-testid="pulldown-icon"
        className={classes.pulldown}
        onClick={e => setOpen(!open)}
      >
        {open ? (
          <ExpandLess data-testid="expand-less-icon" />
        ) : (
          <ExpandMore data-testid="expand-more-icon" />
        )}
      </Avatar>
    </>
  );
};

const NameValue = ({
  name,
  value,
  direction = 'row',
  classes,
}: {
  name: string;
  value: string | string[] | null;
  direction?: 'row' | 'column';
  classes: ClassNameMap;
}) => (
  <Grid item container direction={direction}>
    <Grid item xs={direction === 'row' ? 3 : 12}>
      <Typography>{name}:</Typography>
    </Grid>
    {(Array.isArray(value) ? value : [value]).map((v, idx) => (
      <Grid item xs={direction === 'row' ? 9 : 12} key={`${name}-${idx}`}>
        {v ? (
          <Typography display="inline" className={classes.value}>
            {v}
          </Typography>
        ) : (
          undefined
        )}
      </Grid>
    ))}
  </Grid>
);

const Divider = () => (
  <Grid item style={{ padding: 4 }}>
    <hr style={{ margin: 0 }} />
  </Grid>
);

const SSMServiceSummaryContent = ({
  summary: {
    type,
    name,
    account,
    address,
    fromAddress,
    toAddress,
    date,
    stopDate,
    meters,
  },
}: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isSsmMoveService = window.location.pathname.includes(ROUTES.SSM_MOVE);

  return (
    <Grid
      container
      spacing={1}
      direction="column"
      className={classes.summaryContainer}
    >
      <Hidden smDown>
        <Grid item>
          <Typography className={classes.header}>
            {`${t('SERVICE')} ${t('SUMMARY')}`.toUpperCase()}
          </Typography>
        </Grid>
      </Hidden>

      <Grid item container direction="column">
        <NameValue
          name={t('TYPE')}
          value={t(type)}
          classes={classes}
        ></NameValue>
        {!!name && (
          <NameValue
            name={t('FOR')}
            value={name.replace(/,/g, '')}
            classes={classes}
          ></NameValue>
        )}
        {!!account && (
          <NameValue
            name={t('ACCNT_NO')}
            value={account}
            classes={classes}
          ></NameValue>
        )}
        {address ? <Divider /> : null}
      </Grid>

      <Grid item container direction="column" spacing={2}>
        {!!address && !isSsmMoveService && (
          <NameValue
            name={t('ADDRESS')}
            value={formatServiceAddress(address)?.replace(/,/g, '') || ''}
            direction="column"
            classes={classes}
          ></NameValue>
        )}
        {!!meters && meters.length > 0 && (
          <NameValue
            name={t('FOR')}
            value={meters}
            direction="column"
            classes={classes}
          ></NameValue>
        )}
        {!!fromAddress && (
          <NameValue
            name={`${t('FROM')} ${t('ADDRESS')}`}
            value={fromAddress}
            direction="column"
            classes={classes}
          ></NameValue>
        )}
        {!!fromAddress && !!toAddress && <Divider />}
        {!!toAddress && (
          <NameValue
            name={`${t('TO')} ${t('ADDRESS')}`}
            value={formatServiceAddress(toAddress)?.replace(/,/g, '') || ''}
            direction="column"
            classes={classes}
          ></NameValue>
        )}
        {!!date && (
          <NameValue
            name={t('START_SERVICE_ON')}
            value={format(date, 'MM/dd/yyyy')}
            direction="column"
            classes={classes}
          ></NameValue>
        )}
        {!!stopDate && (
          <NameValue
            name={t('SSM_STOP_SERVICE_ON')}
            value={format(stopDate, 'MM/dd/yyyy')}
            direction="column"
            classes={classes}
          ></NameValue>
        )}
      </Grid>
    </Grid>
  );
};

export const SSMServiceSummary = ({
  summary,
  isPulldown = false,
  service,
}: Props) => {
  const classes = useStyles();
  return (
    <Grid
      container
      item
      direction="column"
      className={isPulldown ? classes.mobile : classes.desktop}
    >
      <Grid item xs={12}>
        {isPulldown ? (
          <Pulldown service={service}>
            <SSMServiceSummaryContent summary={summary} />
          </Pulldown>
        ) : (
          <SSMServiceSummaryContent summary={summary} />
        )}
      </Grid>
    </Grid>
  );
};
